import { useFormik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Row } from "reactstrap";
import Modal from "../../../../components/Common/Modal";
import Input from "../../../../components/Common/Input";
import { TUser } from "../../../../common/types/user";
import { UserRole } from "../../../../constants/user-role";
import { useAddUserAppRole, useRemoveUserAppRole } from "../../../../common/hooks/api/user";
import { updateUser } from "../../../../api/user";
import { toastSuccess, toastError } from "../../../../helpers/toast-helper";
import { TResponseItemWrapper } from "../../../../common/types/common";
import { useQueryClient } from "@tanstack/react-query";

type RoleMutationResult = TResponseItemWrapper<string>;

interface Props {
    isOpen: boolean;
    onToggle: () => void;
    user: TUser | null;
}

const EditUserModal = ({ isOpen, onToggle, user }: Props) => {
    const queryClient = useQueryClient();
    const [isUpdating, setIsUpdating] = useState(false);
    const addRoleMutation = useAddUserAppRole();
    const removeRoleMutation = useRemoveUserAppRole();
    
    // Local state for roles to enable optimistic updates
    const [localRoles, setLocalRoles] = useState<string[] | null>(null);

    const availableRoles = useMemo(() => [
        { label: "Admin", value: UserRole.ADMIN },
        { label: "User", value: UserRole.USER },
        { label: "Dealer", value: UserRole.DEALER },
        { label: "Dev", value: UserRole.DEV },
    ], []);

    // Initialize local roles from user prop when it changes
    useEffect(() => {
        if (user?.appRoles) {
            setLocalRoles(user.appRoles.map(role => role.roleName));
        } else {
            setLocalRoles([]);
        }
    }, [user?.appRoles]);

    const form = useFormik({
        initialValues: {
            firstName: user?.firstName || "",
            lastName: user?.lastName || "",
            email: user?.email || "",
            phone: user?.phone || "",
        },
        enableReinitialize: true,
        onSubmit: async (values) => {
            if (!user) return;

            try {
                setIsUpdating(true);
                // Only send the fields that the API expects - exclude nested objects
                await updateUser({
                    id: user.id,
                    email: values.email,
                    firstName: values.firstName,
                    lastName: values.lastName,
                    displayName: `${values.firstName} ${values.lastName}`,
                    phone: values.phone,
                    created: user.created,
                    operatedDeviceCount: user.operatedDeviceCount,
                    ownedDevices: [],
                    operatedDevices: [],
                    dealerDevices: [],
                    appRoles: [],
                    company: user.company,
                } as TUser);
                toastSuccess("User updated successfully");
                queryClient.invalidateQueries({ queryKey: ['user', user.id] });
                queryClient.invalidateQueries({ queryKey: ['users'] });
                onToggle();
            } catch {
                toastError("Failed to update user");
            } finally {
                setIsUpdating(false);
            }
        },
    });

    // Use localRoles if initialized, otherwise fall back to user prop
    const currentRoles = localRoles !== null ? localRoles : (user?.appRoles?.map(role => role.roleName) || []);

    const handleRoleToggle = async (roleName: string) => {
        if (!user) return;

        const hasRole = currentRoles.includes(roleName);
        
        // Optimistically update local state
        if (hasRole) {
            setLocalRoles(prev => (prev || []).filter(r => r !== roleName));
        } else {
            setLocalRoles(prev => [...(prev || []), roleName]);
        }
        
        try {
            if (hasRole) {
                const result = await removeRoleMutation.mutateAsync({
                    userEmail: user.email,
                    roleName,
                }) as RoleMutationResult;
                if (!result?.success) {
                    throw new Error(result?.errorMessage || `Failed to remove ${roleName} role`);
                }
                toastSuccess(result.data || `Removed ${roleName} role`);
            } else {
                const result = await addRoleMutation.mutateAsync({
                    userEmail: user.email,
                    roleName,
                }) as RoleMutationResult;
                if (!result?.success) {
                    throw new Error(result?.errorMessage || `Failed to add ${roleName} role`);
                }
                toastSuccess(result.data || `Added ${roleName} role`);
            }
            queryClient.invalidateQueries({ queryKey: ['user', user.id] });
            queryClient.invalidateQueries({ queryKey: ['users'] });
        } catch (error) {
            // Revert optimistic update on error
            if (hasRole) {
                setLocalRoles(prev => [...(prev || []), roleName]);
            } else {
                setLocalRoles(prev => (prev || []).filter(r => r !== roleName));
            }
            const message = error instanceof Error
                ? error.message
                : `Failed to ${hasRole ? 'remove' : 'add'} role`;
            toastError(message);
        }
    };

    const handleClose = () => {
        form.resetForm();
        onToggle();
    };

    if (!user) return null;

    return (
        <Modal title="Edit User" isOpen={isOpen} onToggle={handleClose}>
            <Form
                onSubmit={(e) => {
                    e.preventDefault();
                    form.handleSubmit();
                    return false;
                }}
            >
                <Row className="mb-3">
                    <Col xs={12} className="font-size-16 fw-bold pb-2">
                        User Information
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="First Name"
                                name="firstName"
                                placeholder="First Name"
                                form={form}
                                value={form.values.firstName}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Last Name"
                                name="lastName"
                                placeholder="Last Name"
                                form={form}
                                value={form.values.lastName}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Email Address"
                                name="email"
                                placeholder="Email Address"
                                form={form}
                                value={form.values.email}
                            />
                        </div>
                    </Col>
                    <Col xs={12} md={6}>
                        <div className="mb-3">
                            <Input
                                label="Phone Number"
                                name="phone"
                                placeholder="Phone Number"
                                form={form}
                                value={form.values.phone}
                            />
                        </div>
                    </Col>
                </Row>

                <Row className="mb-3">
                    <Col xs={12} className="font-size-16 fw-bold pb-2">
                        App Roles
                    </Col>
                    <Col xs={12}>
                        <div className="mb-3">
                            {availableRoles.map((role) => {
                                const isChecked = currentRoles.includes(role.value);
                                return (
                                    <div key={role.value} className="form-check mb-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleRoleToggle(role.value)}
                                            id={`role-${role.value}`}
                                            disabled={addRoleMutation.isPending || removeRoleMutation.isPending}
                                        />
                                        <label className="form-check-label" htmlFor={`role-${role.value}`}>
                                            {role.label}
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col className="text-end">
                        <Button
                            type="button"
                            color="secondary"
                            className="me-2"
                            onClick={handleClose}
                            disabled={isUpdating}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            color="primary"
                            disabled={isUpdating}
                        >
                            {isUpdating ? "Saving..." : "Save Changes"}
                        </Button>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};

export default EditUserModal;

